// Centralised "trigger a browser download" helper. Used to live as three
// separate implementations: a base64 decoder in BidProjectView, a plain-text
// blob handler in WordWizardTab, and the trailing half of
// KnowledgeBaseService.downloadDoc. All three follow the same dance - build
// a Blob, click an anchor, then revoke the object URL - so they share one
// helper now.

export interface DownloadFileOptions {
  // Either a Blob, a base64-encoded string, or a plain string to download.
  data: Blob | string
  // When `data` is a string and this flag is true, the helper base64-decodes
  // it into a Uint8Array before wrapping in a Blob. Ignored for Blob input.
  base64?: boolean
  // MIME type for the resulting Blob. Defaults to the browser's inference
  // (empty type) for binary, `text/plain` for non-base64 strings.
  mime?: string
  filename: string
}

export function downloadFile({ data, base64 = false, mime, filename }: DownloadFileOptions): void {
  let blob: Blob
  if (data instanceof Blob) {
    blob = data
  } else if (base64) {
    const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0))
    blob = new Blob([bytes], mime ? { type: mime } : undefined)
  } else {
    blob = new Blob([data], { type: mime ?? 'text/plain' })
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  // Some browsers require the anchor to be in the document for the click to
  // dispatch a navigation. Append + remove around the click to be safe.
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
